import React from 'react';
import { Link } from 'react-router-dom';
import LegalPage from '../components/public/LegalPage';
import { SITE, legalEntity } from '../config/site';

// DRAFT for legal review. It describes how the service really works; a South African attorney must review and
// approve it (limits of liability, indemnity, refunds and governing law especially) before launch.
const sections = [
  {
    id: 'about',
    title: 'About these terms',
    body: (
      <>
        <p>
          These terms are an agreement between you and <strong>{legalEntity()}</strong>
          {SITE.registrationNumber && <> (registration number {SITE.registrationNumber})</>} (&quot;we&quot;,
          &quot;us&quot;), who run {SITE.name}. They apply when you use our website or app, open a personal link we email
          you, or create an account.
        </p>
        <p>
          By registering, ticking the box to accept these terms, or continuing to use the service, you agree to them. If
          you do not agree, please do not use the service. How we handle personal information is set out in our{' '}
          <Link to="/privacy">Privacy Policy</Link>, which forms part of this agreement.
        </p>
      </>
    )
  },
  {
    id: 'service',
    title: 'What the service does, and what it does not',
    body: (
      <>
        <p>
          {SITE.name} is a platform that helps landlords and letting agents collect and check information about people
          who apply to rent a property, with the applicant&apos;s informed consent. Applicants can see what is checked, why,
          and who sees the result, and can correct information and raise disputes.
        </p>
        <p>To be clear about what we are not:</p>
        <ul>
          <li>We are not a credit bureau, a lender, a bank or a government service.</li>
          <li>We do not decide who gets a property. The landlord or agent decides, and is responsible for the decision.</li>
          <li>We do not keep or share a public list or &quot;blacklist&quot; of tenants.</li>
          <li>We do not give legal, financial or credit advice.</li>
        </ul>
      </>
    )
  },
  {
    id: 'accounts',
    title: 'Accounts and personal links',
    body: (
      <>
        <p>
          You must be at least 18 and able to enter a binding agreement. Give us accurate information and keep it up to
          date. Keep your password and two-factor sign-in device safe: you are responsible for what happens on your
          account unless you tell us promptly that it has been compromised.
        </p>
        <p>
          Applicants can start or resume an application from a personal link sent to their email address. That link is for
          you alone. Do not forward it or share it, because anyone with the link can act as you on that application. It
          expires, and we may cancel it.
        </p>
        <p>
          Landlord and agent accounts must use two-factor sign-in. People join an organisation only by accepting an
          invitation, and the organisation&apos;s owner is responsible for who they invite and for what their team does.
        </p>
      </>
    )
  },
  {
    id: 'applicants',
    title: 'If you are applying to rent',
    body: (
      <>
        <ul>
          <li>Applying through {SITE.name} is free for applicants.</li>
          <li>Give true and complete information and genuine documents. Giving false information or forged documents is a
            serious breach of these terms and may be a criminal offence.</li>
          <li>You choose which checks to consent to, one by one. Some checks may be required by the landlord for a
            particular property. You are shown which before you agree, and you can withdraw consent at any time under{' '}
            <em>My data</em>. If you withdraw or refuse a check the landlord may not be able to finish assessing your
            application.</li>
          <li>Applying does not guarantee you a property. The landlord decides.</li>
        </ul>
      </>
    )
  },
  {
    id: 'landlords',
    title: 'If you are a landlord or agent',
    body: (
      <>
        <p>You confirm and agree that:</p>
        <ul>
          <li>you have the right to let the property you add, or are authorised to act for the person who does. We may ask
            for proof, and we tell applicants only whether we have verified it;</li>
          <li>you will use applicants&apos; information only to assess their application for that property, and will keep it
            confidential and not pass it on;</li>
          <li>you are responsible for your rental decisions and for making them lawfully and fairly, including complying
            with laws that prohibit unfair discrimination, and for giving applicants the reasons for a decision;</li>
          <li>you will respect applicants&apos; rights, including consent, correction and disputes, and will respond to
            disputes raised about a decision you made;</li>
          <li>you are responsible for the actions of everyone you invite to your organisation.</li>
        </ul>
      </>
    )
  },
  {
    id: 'reports',
    title: 'Checks, reports and their limits',
    body: (
      <>
        <p>
          Reports are built from information applicants give us and from the sources we use to check it. That information
          can be incomplete, out of date or wrong, and a check may be unavailable or inconclusive. A report is an aid to a
          human decision, not a guarantee, and we do not promise that it is complete or error-free.
        </p>
        <p>
          If something in a report is wrong, the applicant can ask for a correction or raise a dispute. We record what
          happens and tell them the outcome.
        </p>
      </>
    )
  },
  {
    id: 'fees',
    title: 'Fees and payment',
    body: (
      <>
        <p>
          Landlords and agents pay for reports and plans. The price, what it includes and any conditions (including
          refunds) are shown before you confirm, and you are not charged for anything you have not confirmed. Prices
          include or exclude VAT as stated at the time. Applicants do not pay.
        </p>
      </>
    )
  },
  {
    id: 'use',
    title: 'Acceptable use',
    body: (
      <>
        <p>You must not:</p>
        <ul>
          <li>break the law, or use the service to unfairly discriminate against anyone;</li>
          <li>give false information, impersonate anyone, or apply, or check someone, without their involvement;</li>
          <li>use information you get from the service for any purpose other than assessing the application it relates to;</li>
          <li>try to get into accounts or data that are not yours, probe or attack our systems, or bypass our security;</li>
          <li>copy or scrape the service, reverse engineer it, or resell access to it;</li>
          <li>upload viruses or other harmful files, or upload files you have no right to.</li>
        </ul>
        <p>If you find a security problem, please tell us through the <Link to="/contact">contact page</Link> rather than exploiting it.</p>
      </>
    )
  },
  {
    id: 'content',
    title: 'Your information and our rights',
    body: (
      <>
        <p>
          You keep ownership of the information and documents you give us. You give us permission to process them to
          provide the service, as described in the Privacy Policy. The service itself, including our software, design and
          text, belongs to us or our licensors, and you may use it only as these terms allow.
        </p>
      </>
    )
  },
  {
    id: 'availability',
    title: 'Availability and changes to the service',
    body: (
      <>
        <p>
          We work to keep the service running, but we do not promise it will always be available or free of errors. We may
          change, suspend or stop parts of it, for example for maintenance, security or because a data source changes. Some
          checks depend on third parties outside our control.
        </p>
      </>
    )
  },
  {
    id: 'liability',
    title: 'Our responsibility',
    body: (
      <>
        <p>
          Nothing in these terms limits or excludes a right or liability that the law does not allow us to limit or
          exclude, including your rights under consumer protection law.
        </p>
        <p>Subject to that, and as far as the law allows:</p>
        <ul>
          <li>the service is provided as it is and as available;</li>
          <li>we are not responsible for a landlord&apos;s or agent&apos;s decisions, or for an applicant&apos;s information being untrue;</li>
          <li>we are not liable for indirect or consequential loss, or for loss of profit or opportunity;</li>
          <li>for paying users, our total liability for any claim is limited to the fees you paid us in the 12 months before the
            claim arose.</li>
        </ul>
        <p>
          You agree to compensate us for losses we suffer because you broke these terms or the law when using the service,
          for example by misusing applicants&apos; information.
        </p>
      </>
    )
  },
  {
    id: 'ending',
    title: 'Suspending or closing an account',
    body: (
      <>
        <p>
          You can stop using the service at any time, and can ask us to delete your data (see the Privacy Policy). We may
          suspend or close an account, or cancel a link, if you break these terms, if we suspect fraud or misuse, or if the
          law requires it. Where we can, we will tell you why. Sections that by their nature should continue, such as those
          on confidentiality, responsibility and disputes, continue after an account ends.
        </p>
      </>
    )
  },
  {
    id: 'changes',
    title: 'Changes to these terms',
    body: (
      <>
        <p>
          We may update these terms. The version and date are at the top of this page. If a change is important we will
          tell you by email or on the website before it takes effect, and by continuing to use the service after that date
          you accept the new version.
        </p>
      </>
    )
  },
  {
    id: 'law',
    title: 'Governing law and disputes',
    body: (
      <>
        <p>
          These terms are governed by the laws of the Republic of South Africa. If you have a complaint, please contact us
          first so we can try to put it right. This does not stop you from going to a court or a statutory body that can
          help, such as the Information Regulator for privacy complaints. Disputes we cannot resolve are to be dealt with by
          the South African courts.
        </p>
      </>
    )
  },
  {
    id: 'contact',
    title: 'Contact',
    body: (
      <>
        <p>
          You can reach us through the <Link to="/contact">contact page</Link>
          {SITE.supportEmail && <> or at <a href={`mailto:${SITE.supportEmail}`}>{SITE.supportEmail}</a></>}.
          {SITE.address && <> Our address is {SITE.address}.</>}
        </p>
      </>
    )
  }
];

const summary = [
  'We help landlords and agents check rental applicants with the applicant’s consent. The landlord decides, not us.',
  'Applying is free. Landlords and agents pay, and see the price before they confirm.',
  'Be honest, keep your personal link private, and only use applicants’ information to assess their application.',
  'We are not a credit bureau and we do not keep a blacklist.'
];

const TermsPage = () => (
  <LegalPage
    title="Terms and Conditions"
    description="The terms for using TenantVerify SA, for applicants, landlords and agents."
    summary={summary}
    sections={sections}
  />
);

export default TermsPage;
